import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  BookOpenCheck,
  Building2,
  Check,
  ClipboardList,
  Copy,
  KeyRound,
  Layers3,
  LogOut,
  Plus,
  ShieldCheck,
  UserPlus,
  Users,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

const TABS = ["overview", "learners", "cohorts", "courses"];

export default function LMSOrganisationAdmin() {
  const { organisationId } = useParams();
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [learners, setLearners] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseAccess, setCourseAccess] = useState([]);

  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [credential, setCredential] = useState(null);

  const [showLearnerForm, setShowLearnerForm] = useState(false);
  const [showCohortForm, setShowCohortForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);

  const [learnerForm, setLearnerForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    learner_reference: "",
    cohort_ids: [],
  });

  const [cohortForm, setCohortForm] = useState({
    name: "",
    code: "",
  });

  const [adminForm, setAdminForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
  });

  const isPlatformAdmin = Boolean(me?.is_platform_admin);

  const membership = useMemo(
    () =>
      me?.memberships?.find(
        (item) => item.organisation_id === organisationId
      ),
    [me, organisationId]
  );

  const canWrite =
    isPlatformAdmin ||
    ["organisation_admin", "instructor"].includes(
      membership?.role
    );

  const load = async () => {
    setError("");

    try {
      const meResponse = await lmsApi.get("/auth/me");
      const currentUser = meResponse.data;

      if (currentUser.require_password_change) {
        navigate("/learn/change-password", {
          replace: true,
        });
        return;
      }

      setMe(currentUser);

      const [
        dashboardResponse,
        learnerResponse,
        cohortResponse,
        courseResponse,
      ] = await Promise.all([
        lmsApi.get(
          `/manage/organisations/${organisationId}/dashboard`
        ),
        lmsApi.get(
          `/manage/organisations/${organisationId}/learners`
        ),
        lmsApi.get(
          `/manage/organisations/${organisationId}/cohorts`
        ),
        lmsApi.get(
          `/manage/organisations/${organisationId}/courses`
        ),
      ]);

      setDashboard(dashboardResponse.data);
      setLearners(learnerResponse.data);
      setCohorts(cohortResponse.data);
      setCourses(courseResponse.data);

      if (currentUser.is_platform_admin) {
        const accessResponse = await lmsApi.get(
          `/platform/organisations/${organisationId}/course-access`
        );

        setCourseAccess(accessResponse.data);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/learn/login", {
          replace: true,
        });
        return;
      }

      setError(
        err.response?.data?.detail ||
          "We could not load this organisation workspace."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  const refreshWorkspace = async () => {
    const [
      dashboardResponse,
      learnerResponse,
      cohortResponse,
      courseResponse,
    ] = await Promise.all([
      lmsApi.get(
        `/manage/organisations/${organisationId}/dashboard`
      ),
      lmsApi.get(
        `/manage/organisations/${organisationId}/learners`
      ),
      lmsApi.get(
        `/manage/organisations/${organisationId}/cohorts`
      ),
      lmsApi.get(
        `/manage/organisations/${organisationId}/courses`
      ),
    ]);

    setDashboard(dashboardResponse.data);
    setLearners(learnerResponse.data);
    setCohorts(cohortResponse.data);
    setCourses(courseResponse.data);
  };

  const createLearner = async (event) => {
    event.preventDefault();

    setError("");
    setMessage("");

    try {
      const response = await lmsApi.post(
        `/manage/organisations/${organisationId}/learners`,
        {
          ...learnerForm,
          username: learnerForm.username || null,
          email: learnerForm.email || null,
          learner_reference:
            learnerForm.learner_reference || null,
        }
      );

      setCredential({
        title: "Learner login created",
        ...response.data,
      });

      setLearnerForm({
        first_name: "",
        last_name: "",
        username: "",
        email: "",
        learner_reference: "",
        cohort_ids: [],
      });

      setShowLearnerForm(false);

      await refreshWorkspace();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The learner could not be created."
      );
    }
  };

  const createCohort = async (event) => {
    event.preventDefault();

    setError("");

    try {
      await lmsApi.post(
        `/manage/organisations/${organisationId}/cohorts`,
        {
          name: cohortForm.name,
          code: cohortForm.code || null,
        }
      );

      setCohortForm({
        name: "",
        code: "",
      });

      setShowCohortForm(false);

      setMessage("Cohort created.");

      await refreshWorkspace();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The cohort could not be created."
      );
    }
  };

  const createOrganisationAdmin = async (event) => {
    event.preventDefault();

    setError("");

    try {
      const response = await lmsApi.post(
        `/platform/organisations/${organisationId}/admins`,
        {
          ...adminForm,
          email: adminForm.email || null,
        }
      );

      setCredential({
        title: "Organisation administrator created",
        ...response.data,
      });

      setAdminForm({
        first_name: "",
        last_name: "",
        username: "",
        email: "",
      });

      setShowAdminForm(false);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "The administrator could not be created."
      );
    }
  };

  const toggleCourseAccess = async (course) => {
    setError("");

    try {
      if (course.access_granted) {
        await lmsApi.delete(
          `/platform/organisations/${organisationId}/course-access/${course.course_id}`
        );
      } else {
        await lmsApi.put(
          `/platform/organisations/${organisationId}/course-access/${course.course_id}`
        );
      }

      const response = await lmsApi.get(
        `/platform/organisations/${organisationId}/course-access`
      );

      setCourseAccess(response.data);

      await refreshWorkspace();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Course access could not be changed."
      );
    }
  };

  const updateOrganisationStatus = async (status) => {
    setError("");

    try {
      await lmsApi.patch(
        `/platform/organisations/${organisationId}`,
        {
          status,
        }
      );

      setMessage(
        `Organisation status changed to ${status}.`
      );

      await refreshWorkspace();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Organisation status could not be changed."
      );
    }
  };

  const handleLogout = async () => {
    try {
      await lmsApi.post("/auth/logout");
    } finally {
      navigate("/learn/login", {
        replace: true,
      });
    }
  };

  const copyCredential = async () => {
    if (!credential) {
      return;
    }

    await navigator.clipboard?.writeText(
      `Username: ${credential.username}\nTemporary password: ${credential.temporary_password}`
    );

    setMessage(
      "Login details copied. The temporary password is only shown here once."
    );
  };

  if (loading) {
    return (
      <section className="min-h-[60vh] bg-slate-50 py-20">
        <div className="container-rail text-center text-slate-600">
          Loading organisation workspace…
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title={`${
          dashboard?.organisation_name || "Organisation"
        } | TRAC-Learn`}
        description="Secure TRAC-Learn organisation administration."
        path={`/learn/admin/organisations/${organisationId}`}
      />

      <section className="bg-navy py-9 text-white md:py-12">
        <div className="container-rail">
          <div className="flex flex-col justify-between gap-5 md:flex-row md:items-center">
            <div>
              {isPlatformAdmin && (
                <button
                  type="button"
                  onClick={() =>
                    navigate("/learn/admin")
                  }
                  className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-white/65 hover:text-white"
                >
                  <ArrowLeft className="h-4 w-4" />
                  All organisations
                </button>
              )}

              <p className="label-eyebrow text-safety">
                TRAC-Learn organisation
              </p>

              <h1 className="mt-2 font-display text-4xl font-bold md:text-5xl">
                {dashboard?.organisation_name}
              </h1>

              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-white/65">
                <span>
                  {dashboard?.organisation_type?.replaceAll(
                    "_",
                    " "
                  )}
                </span>

                <span>•</span>

                <span>
                  {dashboard?.organisation_slug}
                </span>

                <span
                  className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                    dashboard?.status === "active"
                      ? "bg-emerald-400/15 text-emerald-200"
                      : "bg-amber-400/15 text-amber-200"
                  }`}
                >
                  {dashboard?.status}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white">
        <div className="container-rail flex gap-1 overflow-x-auto py-2">
          {[
            ...TABS,
            ...(isPlatformAdmin
              ? ["access"]
              : []),
          ].map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setTab(item)}
              className={`whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-bold capitalize ${
                tab === item
                  ? "bg-navy text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {item === "access"
                ? "UKTDA access"
                : item}
            </button>
          ))}
        </div>
      </section>

      <section className="min-h-[55vh] bg-slate-50 py-8 md:py-12">
        <div className="container-rail">
          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          {message && (
            <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              {message}
            </div>
          )}

          {credential && (
            <div className="mb-8 rounded-2xl border-2 border-safety bg-white p-6 shadow-sm">
              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                <div>
                  <div className="flex items-center gap-2 text-safety">
                    <KeyRound className="h-5 w-5" />

                    <p className="text-sm font-bold">
                      {credential.title}
                    </p>
                  </div>

                  <p className="mt-3 text-sm text-slate-600">
                    Record these details securely now. The temporary password
                    is not stored in readable form and cannot be displayed
                    again.
                  </p>

                  <div className="mt-4 grid gap-3 font-mono text-sm sm:grid-cols-2">
                    <div className="rounded-lg bg-slate-50 p-3">
                      <span className="block text-xs text-slate-500">
                        Username
                      </span>

                      {credential.username}
                    </div>

                    <div className="rounded-lg bg-slate-50 p-3">
                      <span className="block text-xs text-slate-500">
                        Temporary password
                      </span>

                      {credential.temporary_password}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={copyCredential}
                    className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2.5 text-sm font-bold text-white"
                  >
                    <Copy className="h-4 w-4" />
                    Copy
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      setCredential(null)
                    }
                    className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === "overview" && (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  [
                    Users,
                    "Learners",
                    dashboard?.learner_count || 0,
                  ],
                  [
                    Layers3,
                    "Cohorts",
                    dashboard?.cohort_count || 0,
                  ],
                  [
                    BookOpenCheck,
                    "Courses",
                    dashboard?.course_count || 0,
                  ],
                  [
                    ClipboardList,
                    "Enrolments",
                    dashboard?.enrolment_count || 0,
                  ],
                ].map(([Icon, label, value]) => (
                  <div
                    key={label}
                    className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                  >
                    <div className="flex items-center gap-3 text-sm font-semibold text-slate-500">
                      <Icon className="h-5 w-5 text-safety" />
                      {label}
                    </div>

                    <p className="mt-3 font-display text-3xl font-bold text-navy">
                      {value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid gap-6 lg:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <p className="label-eyebrow text-safety">
                    Learning status
                  </p>

                  <h2 className="mt-2 font-display text-2xl font-bold text-navy">
                    Current enrolments
                  </h2>

                  <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-2xl font-bold text-navy">
                        {dashboard?.not_started_count ||
                          0}
                      </p>

                      <p className="mt-1 text-xs text-slate-500">
                        Not started
                      </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-2xl font-bold text-navy">
                        {dashboard?.in_progress_count ||
                          0}
                      </p>

                      <p className="mt-1 text-xs text-slate-500">
                        In progress
                      </p>
                    </div>

                    <div className="rounded-xl bg-slate-50 p-4">
                      <p className="text-2xl font-bold text-navy">
                        {dashboard?.completed_count ||
                          0}
                      </p>

                      <p className="mt-1 text-xs text-slate-500">
                        Completed
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-2 text-safety">
                    <ShieldCheck className="h-5 w-5" />

                    <p className="label-eyebrow">
                      Access boundary
                    </p>
                  </div>

                  <h2 className="mt-2 font-display text-2xl font-bold text-navy">
                    Organisation-isolated administration
                  </h2>

                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    Learners, cohorts, enrolments and course access are scoped
                    to this organisation. Organisation users cannot administer
                    another organisation by changing a URL or identifier.
                  </p>
                </div>
              </div>
            </>
          )}

          {tab === "learners" && (
            <div>
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
                <div>
                  <p className="label-eyebrow text-safety">
                    Learner management
                  </p>

                  <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                    Learners
                  </h2>
                </div>

                {canWrite && (
                  <button
                    type="button"
                    onClick={() =>
                      setShowLearnerForm(
                        (value) => !value
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-3 text-sm font-bold text-white"
                  >
                    <UserPlus className="h-4 w-4" />
                    Add learner
                  </button>
                )}
              </div>

              {showLearnerForm && (
                <form
                  onSubmit={createLearner}
                  className="mt-6 grid gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:grid-cols-2"
                >
                  <input
                    required
                    placeholder="First name"
                    value={learnerForm.first_name}
                    onChange={(e) =>
                      setLearnerForm((v) => ({
                        ...v,
                        first_name: e.target.value,
                      }))
                    }
                    className="rounded-lg border border-slate-300 px-4 py-3"
                  />

                  <input
                    required
                    placeholder="Last name"
                    value={learnerForm.last_name}
                    onChange={(e) =>
                      setLearnerForm((v) => ({
                        ...v,
                        last_name: e.target.value,
                      }))
                    }
                    className="rounded-lg border border-slate-300 px-4 py-3"
                  />

                  <input
                    placeholder="Username (optional – generated if blank)"
                    value={learnerForm.username}
                    onChange={(e) =>
                      setLearnerForm((v) => ({
                        ...v,
                        username: e.target.value,
                      }))
                    }
                    className="rounded-lg border border-slate-300 px-4 py-3"
                  />

                  <input
                    type="email"
                    placeholder="Email (optional)"
                    value={learnerForm.email}
                    onChange={(e) =>
                      setLearnerForm((v) => ({
                        ...v,
                        email: e.target.value,
                      }))
                    }
                    className="rounded-lg border border-slate-300 px-4 py-3"
                  />

                  <input
                    placeholder="Learner/reference ID (optional)"
                    value={
                      learnerForm.learner_reference
                    }
                    onChange={(e) =>
                      setLearnerForm((v) => ({
                        ...v,
                        learner_reference:
                          e.target.value,
                      }))
                    }
                    className="rounded-lg border border-slate-300 px-4 py-3 md:col-span-2"
                  />

                  {cohorts.length > 0 && (
                    <div className="md:col-span-2">
                      <p className="mb-2 text-sm font-bold text-navy">
                        Cohorts
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {cohorts
                          .filter(
                            (cohort) =>
                              cohort.status ===
                              "active"
                          )
                          .map((cohort) => {
                            const selected =
                              learnerForm.cohort_ids.includes(
                                cohort.id
                              );

                            return (
                              <button
                                key={cohort.id}
                                type="button"
                                onClick={() =>
                                  setLearnerForm(
                                    (v) => ({
                                      ...v,
                                      cohort_ids:
                                        selected
                                          ? v.cohort_ids.filter(
                                              (
                                                id
                                              ) =>
                                                id !==
                                                cohort.id
                                            )
                                          : [
                                              ...v.cohort_ids,
                                              cohort.id,
                                            ],
                                    })
                                  )
                                }
                                className={`rounded-full border px-3 py-2 text-xs font-bold ${
                                  selected
                                    ? "border-safety bg-safety/10 text-navy"
                                    : "border-slate-300 text-slate-600"
                                }`}
                              >
                                {selected && (
                                  <Check className="mr-1 inline h-3 w-3" />
                                )}

                                {cohort.name}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 md:col-span-2">
                    <button
                      type="submit"
                      className="rounded-lg bg-safety px-5 py-3 text-sm font-bold text-white"
                    >
                      Create learner
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setShowLearnerForm(false)
                      }
                      className="rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                {learners.length ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-5 py-4">
                            Learner
                          </th>

                          <th className="px-5 py-4">
                            Username
                          </th>

                          <th className="px-5 py-4">
                            Cohorts
                          </th>

                          <th className="px-5 py-4">
                            Enrolments
                          </th>

                          <th className="px-5 py-4">
                            Completed
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-100">
                        {learners.map(
                          (learner) => (
                            <tr
                              key={
                                learner.id
                              }
                            >
                              <td className="px-5 py-4">
                                <p className="font-bold text-navy">
                                  {
                                    learner.first_name
                                  }{" "}
                                  {
                                    learner.last_name
                                  }
                                </p>

                                <p className="text-xs text-slate-500">
                                  {learner.email ||
                                    learner.learner_reference ||
                                    "No email/reference"}
                                </p>
                              </td>

                              <td className="px-5 py-4 font-mono text-xs text-slate-600">
                                {
                                  learner.username
                                }
                              </td>

                              <td className="px-5 py-4 text-slate-600">
                                {
                                  learner
                                    .cohort_ids
                                    .length
                                }
                              </td>

                              <td className="px-5 py-4 font-semibold text-navy">
                                {
                                  learner.enrolment_count
                                }
                              </td>

                              <td className="px-5 py-4 font-semibold text-navy">
                                {
                                  learner.completed_count
                                }
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-10 text-center text-slate-600">
                    No learners have been created in this organisation.
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "cohorts" && (
            <div>
              <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
                <div>
                  <p className="label-eyebrow text-safety">
                    Group learning
                  </p>

                  <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                    Cohorts
                  </h2>
                </div>

                {canWrite && (
                  <button
                    type="button"
                    onClick={() =>
                      setShowCohortForm(
                        (value) => !value
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-3 text-sm font-bold text-white"
                  >
                    <Plus className="h-4 w-4" />
                    New cohort
                  </button>
                )}
              </div>

              {showCohortForm && (
                <form
                  onSubmit={createCohort}
                  className="mt-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:flex-row"
                >
                  <input
                    required
                    placeholder="Cohort name"
                    value={cohortForm.name}
                    onChange={(e) =>
                      setCohortForm((v) => ({
                        ...v,
                        name: e.target.value,
                      }))
                    }
                    className="flex-1 rounded-lg border border-slate-300 px-4 py-3"
                  />

                  <input
                    placeholder="Code (optional)"
                    value={cohortForm.code}
                    onChange={(e) =>
                      setCohortForm((v) => ({
                        ...v,
                        code: e.target.value,
                      }))
                    }
                    className="rounded-lg border border-slate-300 px-4 py-3"
                  />

                  <button
                    type="submit"
                    className="rounded-lg bg-safety px-5 py-3 text-sm font-bold text-white"
                  >
                    Create
                  </button>
                </form>
              )}

              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {cohorts.map((cohort) => (
                  <div
                    key={cohort.id}
                    className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                  >
                    <Layers3 className="h-6 w-6 text-safety" />

                    <h3 className="mt-4 font-display text-xl font-bold text-navy">
                      {cohort.name}
                    </h3>

                    <p className="mt-1 text-xs font-mono text-slate-400">
                      {cohort.code ||
                        "No code"}
                    </p>

                    <p className="mt-5 text-sm text-slate-600">
                      <strong className="text-navy">
                        {cohort.member_count}
                      </strong>{" "}
                      learners
                    </p>
                  </div>
                ))}
              </div>

              {!cohorts.length && (
                <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-600">
                  Create a cohort to group learners by year, intake, team,
                  programme or another organisational structure.
                </div>
              )}
            </div>
          )}

          {tab === "courses" && (
            <div>
              <p className="label-eyebrow text-safety">
                Course layer
              </p>

              <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                Available courses
              </h2>

              <p className="mt-3 max-w-3xl text-slate-600">
                Only courses made available to this organisation by UKTDA, or
                courses owned by the organisation itself, appear here.
              </p>

              <div className="mt-6 grid gap-5 lg:grid-cols-2">
                {courses.map((course) => (
                  <article
                    key={course.course_id}
                    className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                  >
                    <BookOpenCheck className="h-6 w-6 text-safety" />

                    <h3 className="mt-4 font-display text-2xl font-bold text-navy">
                      {course.title}
                    </h3>

                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                      {course.description ||
                        "No course description yet."}
                    </p>

                    <div className="mt-5 flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-600">
                        {course.latest_published_version
                          ? `Version ${course.latest_published_version}`
                          : "No published version"}
                      </span>

                      {course.pass_mark != null && (
                        <span className="rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-600">
                          Pass mark{" "}
                          {
                            course.pass_mark
                          }
                          %
                        </span>
                      )}

                      {course.estimated_minutes !=
                        null && (
                        <span className="rounded-full bg-slate-100 px-3 py-1.5 font-semibold text-slate-600">
                          {
                            course.estimated_minutes
                          }{" "}
                          min
                        </span>
                      )}
                    </div>
                  </article>
                ))}
              </div>

              {!courses.length && (
                <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-600">
                  No courses have been made available to this organisation yet.
                </div>
              )}
            </div>
          )}

          {tab === "access" &&
            isPlatformAdmin && (
              <div className="grid gap-7 xl:grid-cols-[1fr_1.2fr]">
                <div className="space-y-6">
                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center gap-2 text-safety">
                      <Building2 className="h-5 w-5" />

                      <p className="label-eyebrow">
                        UKTDA gate
                      </p>
                    </div>

                    <h2 className="mt-2 font-display text-2xl font-bold text-navy">
                      Organisation access
                    </h2>

                    <p className="mt-3 text-sm leading-relaxed text-slate-600">
                      UKTDA controls whether this organisation can access
                      TRAC-Learn. Suspending the organisation blocks its own
                      users while retaining its records.
                    </p>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {[
                        "active",
                        "suspended",
                        "archived",
                      ].map((status) => (
                        <button
                          key={status}
                          type="button"
                          onClick={() =>
                            updateOrganisationStatus(
                              status
                            )
                          }
                          className={`rounded-lg px-4 py-2.5 text-sm font-bold capitalize ${
                            dashboard?.status ===
                            status
                              ? "bg-navy text-white"
                              : "border border-slate-300 text-slate-600"
                          }`}
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="label-eyebrow text-safety">
                          Initial access
                        </p>

                        <h2 className="mt-2 font-display text-2xl font-bold text-navy">
                          Organisation administrator
                        </h2>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setShowAdminForm(
                            (value) =>
                              !value
                          )
                        }
                        className="inline-flex items-center gap-2 rounded-lg bg-navy px-3 py-2.5 text-sm font-bold text-white"
                      >
                        <UserPlus className="h-4 w-4" />
                        Create
                      </button>
                    </div>

                    <p className="mt-3 text-sm text-slate-600">
                      Create the organisation&apos;s first administrator. They
                      receive a one-time temporary password and must replace it
                      on first login.
                    </p>

                    {showAdminForm && (
                      <form
                        onSubmit={
                          createOrganisationAdmin
                        }
                        className="mt-5 grid gap-3"
                      >
                        <input
                          required
                          placeholder="First name"
                          value={
                            adminForm.first_name
                          }
                          onChange={(e) =>
                            setAdminForm(
                              (v) => ({
                                ...v,
                                first_name:
                                  e.target.value,
                              })
                            )
                          }
                          className="rounded-lg border border-slate-300 px-4 py-3"
                        />

                        <input
                          required
                          placeholder="Last name"
                          value={
                            adminForm.last_name
                          }
                          onChange={(e) =>
                            setAdminForm(
                              (v) => ({
                                ...v,
                                last_name:
                                  e.target.value,
                              })
                            )
                          }
                          className="rounded-lg border border-slate-300 px-4 py-3"
                        />

                        <input
                          required
                          placeholder="Username"
                          value={
                            adminForm.username
                          }
                          onChange={(e) =>
                            setAdminForm(
                              (v) => ({
                                ...v,
                                username:
                                  e.target.value,
                              })
                            )
                          }
                          className="rounded-lg border border-slate-300 px-4 py-3"
                        />

                        <input
                          type="email"
                          placeholder="Email (optional)"
                          value={
                            adminForm.email
                          }
                          onChange={(e) =>
                            setAdminForm(
                              (v) => ({
                                ...v,
                                email:
                                  e.target.value,
                              })
                            )
                          }
                          className="rounded-lg border border-slate-300 px-4 py-3"
                        />

                        <button
                          type="submit"
                          className="rounded-lg bg-safety px-4 py-3 text-sm font-bold text-white"
                        >
                          Create administrator
                        </button>
                      </form>
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                  <p className="label-eyebrow text-safety">
                    Course entitlement
                  </p>

                  <h2 className="mt-2 font-display text-2xl font-bold text-navy">
                    Available course catalogue
                  </h2>

                  <p className="mt-3 text-sm text-slate-600">
                    Grant or revoke organisation-level access. Enrolment is
                    handled separately inside the organisation workspace.
                  </p>

                  <div className="mt-5 divide-y divide-slate-100">
                    {courseAccess.map(
                      (course) => (
                        <div
                          key={
                            course.course_id
                          }
                          className="flex items-center justify-between gap-4 py-4"
                        >
                          <div>
                            <p className="font-bold text-navy">
                              {
                                course.title
                              }
                            </p>

                            <p className="mt-1 text-xs text-slate-500">
                              {course.latest_published_version
                                ? `Latest published version ${course.latest_published_version}`
                                : "No published version"}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() =>
                              toggleCourseAccess(
                                course
                              )
                            }
                            className={`rounded-lg px-3 py-2 text-xs font-bold ${
                              course.access_granted
                                ? "bg-emerald-50 text-emerald-700"
                                : "border border-slate-300 text-slate-600"
                            }`}
                          >
                            {course.access_granted
                              ? "Access granted"
                              : "Grant access"}
                          </button>
                        </div>
                      )
                    )}
                  </div>

                  {!courseAccess.length && (
                    <div className="mt-5 rounded-xl bg-slate-50 p-5 text-sm text-slate-600">
                      No courses exist in the platform catalogue yet. This
                      control will populate as UKTDA publishes courses.
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
      </section>
    </>
  );
}